// ----- Phone -----------------------------------------------------------

/**
 * Format an Uzbek mobile phone for display: `+998 (XX) XXX-XX-XX`.
 * Pass any string; non-digits are ignored. Always preserves +998 prefix.
 */
export function formatPhoneUz(raw: string): string {
  // Strip everything to digits, drop leading "998" if present
  const digits = raw.replace(/\D/g, "").replace(/^998/, "").slice(0, 9);
  let out = "+998";
  if (digits.length === 0) return out + " ";
  out += " (" + digits.slice(0, 2);
  if (digits.length >= 2) out += ")";
  if (digits.length > 2) out += " " + digits.slice(2, 5);
  if (digits.length > 5) out += "-" + digits.slice(5, 7);
  if (digits.length > 7) out += "-" + digits.slice(7, 9);
  return out;
}

/** Extract just the digits in canonical form: `+998901234567`. */
export function canonicalPhoneUz(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^998/, "").slice(0, 9);
  if (digits.length === 0) return "";
  return "+998" + digits;
}

// ----- Passport (UZ format AA1234567) -----------------------------------

/** Display "AA 1234567" given any input. */
export function formatPassportUz(raw: string): string {
  const cleaned = raw.replace(/\s/g, "").toUpperCase();
  const seriya = cleaned.slice(0, 2).replace(/[^A-Z]/g, "");
  const number = cleaned.slice(2, 9).replace(/\D/g, "");
  if (!seriya) return "";
  if (seriya.length < 2) return seriya;
  if (number.length === 0) return seriya + " ";
  return seriya + " " + number;
}

export function passportParts(raw: string): { seriya: string; number: string } {
  const cleaned = raw.replace(/\s/g, "").toUpperCase();
  const seriya = cleaned.slice(0, 2).replace(/[^A-Z]/g, "");
  const number = cleaned.slice(2, 9).replace(/\D/g, "");
  return { seriya, number };
}

// ----- Date / time ------------------------------------------------------

/** Format a date as `DD.MM.YYYY`. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
}

/** Format a date-time as `DD.MM.YYYY HH:MM`. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mn = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yy} ${hh}:${mn}`;
}

const WEEKDAYS_UZ = [
  "Yakshanba",
  "Dushanba",
  "Seshanba",
  "Chorshanba",
  "Payshanba",
  "Juma",
  "Shanba",
];

const WEEKDAYS_RU = [
  "Воскресенье",
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
];

const WEEKDAYS_EN = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Long form: `05.05.2026 Chorshanba` (with localized weekday). */
export function formatDateWithWeekday(
  value: string | Date | null | undefined,
  lang: string,
): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return "—";
  const list =
    lang === "ru" ? WEEKDAYS_RU : lang === "en" ? WEEKDAYS_EN : WEEKDAYS_UZ;
  return `${formatDate(d)} ${list[d.getDay()]}`;
}

const MONTHS_UZ = [
  "Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun",
  "Iyul", "Avgust", "Sentabr", "Oktabr", "Noyabr", "Dekabr",
];
const MONTHS_RU = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function formatMonthYear(
  value: string | Date | null | undefined,
  lang: string,
): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (isNaN(d.getTime())) return "—";
  const list =
    lang === "ru" ? MONTHS_RU : lang === "en" ? MONTHS_EN : MONTHS_UZ;
  return `${list[d.getMonth()]} ${d.getFullYear()}`;
}
